import React from 'react';
import { Button, Form, Typography } from 'antd';
import type { AdminProductFormValues, AdminProductMaterialsFieldsProps} from '../../interfaces';
import MaterialDetailsCollapse from './MaterialDetailsCollapse';
import MaterialSummaryRow from './MaterialSummaryRow';
import './AdminProductMaterialsFields.scss';

const { Text } = Typography;

const AdminProductMaterialsFields: React.FC<AdminProductMaterialsFieldsProps> = ({
  materialTemplates = [],
  filteredSizes,
  invalidMaterialIndexes = [],
  requiresSizes,
}) => {
  const form = Form.useFormInstance<AdminProductFormValues>();
  const handleMaterialChange = (rowIndex: number, value: string) => {
    const selectedMaterial = materialTemplates.find((material) => material.materialName === value);
    const materials = form.getFieldValue('materials') || [];

    form.setFieldsValue({
      materials: materials.map((material: AdminProductFormValues['materials'][number], materialIndex: number) =>
        materialIndex === rowIndex
          ? { ...material, materialName: value, materialIconId: selectedMaterial?.id }
          : material
      ),
    });
  };

  return (
    <Form.List
    name="materials"
    rules={[{
      validator: async (_, materialRows) => {
        if (!materialRows || materialRows.length < 1) {
          return Promise.reject(new Error('At least one material is required.'));
        }
        return Promise.resolve();
      },
    }]}
  >
    {(fields, { add, remove }, { errors }) => {
      const allowedSizeIds = filteredSizes.map((size) => size.id);
      const createEmptyMaterial = () => ({
        materialName: '',
        price: 0,
        stockQuantity: 0,
        sizeIds: requiresSizes ? filteredSizes.map((size) => size.id) : [],
        variantStocks: {},
        images: [],
      });

      return (
      <>
        <div className="admin-product-form__material-add-row">
          <Button onClick={() => add(createEmptyMaterial())}>Add Material Variety</Button>
          <Text type="secondary">{fields.length} material{fields.length === 1 ? '' : 's'} configured</Text>
        </div>
        {fields.map((field, index) => {
          const isInvalidMaterial = invalidMaterialIndexes.includes(field.name);

          return (
          <div
            key={field.key}
            className={`admin-product-form__material-block ${isInvalidMaterial ? 'admin-product-form__material-block--invalid' : ''}`}
          >
            <MaterialSummaryRow
              field={field}
              index={index}
              materialTemplates={materialTemplates}
              requiresSizes={requiresSizes}
              onMaterialChange={handleMaterialChange}
              onRemove={remove}
            />
            <MaterialDetailsCollapse
              field={field}
              forceOpen={isInvalidMaterial}
              materialIndex={field.name}
              allowedSizeIds={allowedSizeIds}
              filteredSizes={filteredSizes}
              requiresSizes={requiresSizes}
            />
          </div>
          );
        })}
        <Form.ErrorList errors={errors} />
      </>
      );
    }}
    </Form.List>
  );
};

export default AdminProductMaterialsFields;
